import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs';
import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonHeader,
    IonIcon,
    IonMenuToggle,
    IonToolbar,
    NavController,
    PopoverController,
} from '@ionic/angular';
import { UserRoles } from '../../../user/entities/user-roles';
import { CurafidaAuthService } from '../../../authentication/curafida-auth.service';
import { BasePageConfig } from '../../../config/entities';
import { CurafidaConnectionState, NetworkService } from '../../../measurement/services/network/network.service';
import { RoutingSegment } from '../../entities/routing-segment';
import { MenuService } from '../../services/menu.service';
import { ToolbarService } from '../../services/toolbar/toolbar.service';
import { PopoverMenuComponent } from '../popover-menu/popover-menu.component';
import { ConfigService } from '../../../config/services/config.service';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MenuItemsFilterPipe } from '../../../config/pipes/menu-items-filter.pipe';

@Component({
    selector: 'lib-curafida-toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        IonToolbar,
        NgClass,
        IonButtons,
        IonMenuToggle,
        IonButton,
        IonIcon,
        IonBackButton,
        TranslatePipe,
        MenuItemsFilterPipe,
        RouterLink,
        IonHeader,
    ],
})
export class ToolbarComponent {
    private readonly toolbarService = inject(ToolbarService);
    private readonly router = inject(Router);
    private readonly popoverController = inject(PopoverController);
    private readonly authService = inject(CurafidaAuthService);
    private readonly navController = inject(NavController);
    private readonly networkService = inject(NetworkService);
    readonly menuService = inject(MenuService);
    readonly configService = inject(ConfigService);

    readonly appConfig = this.configService.frontendConfig;
    readonly patientActionButtons = this.toolbarService.patientActionButtons;

    private readonly networkStatus = toSignal(this.networkService.onNetworkChange(), {
        initialValue: this.networkService.getCurrentNetworkStatus(),
    });
    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => this.router.url),
        ),
        { initialValue: this.router.url },
    );

    readonly showOfflineIcon = computed<boolean>(() => this.networkStatus() === CurafidaConnectionState.OFFLINE);
    readonly roles = computed<UserRoles[]>(() => {
        const roles = this.authService.loggedInUser()?.roles;
        return roles ?? [];
    });
    readonly isPatient = computed<boolean>(() => this.roles().some((role) => role === UserRoles.PATIENT));
    readonly isLoginPage = computed<boolean>(() => this.currentUrl().includes('/login'));
    readonly isRootPage = computed<boolean>(() => {
        const currentUrl = this.currentUrl();
        if (this.roles().length === 0 && currentUrl === '/login') {
            return true;
        }
        if (currentUrl === this.appConfig().defaultUrl) {
            return true;
        }
        return this.appConfig().rootUrls?.some((url) => currentUrl.includes(url.url));
    });

    goToPreviousPage(): void {
        this.navController.pop();
    }

    openPage(url: string): void {
        this.router.navigate([url]);
    }

    mainTitleActive(categories: RoutingSegment[]): boolean {
        for (const category of categories) {
            if (this.router.url.includes(category)) {
                return true;
            }
        }
        return false;
    }

    openProfile(): void {
        this.router.navigate([RoutingSegment.MEMBER, RoutingSegment.PROFILE]);
    }

    async presentPopover(ev: Event, subPagesConfig: BasePageConfig[]): Promise<void> {
        subPagesConfig = this.menuService.fetchSubPageConfig(subPagesConfig, this.roles());
        const popover = await this.popoverController.create({
            component: PopoverMenuComponent,
            translucent: true,
            event: ev,
            componentProps: {
                subPages: subPagesConfig,
            },
        });
        return await popover.present();
    }
}
